import { useEffect, useRef, useCallback, useSyncExternalStore } from "react";
import { mediaFieldsOf, type ChatMedia } from "../utils/chatMedia";
import PubNub, { type Listener } from "pubnub";
import type { ChatLink, ChatPreview, Message } from "../types/chat";
import { readChatLink, readChatPreview } from "../utils/chatLink";
import type { ReactionEventPayload } from "../utils/reactions";
import type { DeletionEventPayload } from "../utils/deletions";
import { isRenderableMessage } from "../utils/liveMessage";
import logger from "../lib/logger";

let pubnubInstance: PubNub | null = null;

// Registered by pubnubBootstrap after init — avoids circular import
let _onAccessDenied: (() => Promise<void>) | null = null;
let _accessDeniedInProgress = false;

export function registerAccessDeniedHandler(cb: () => Promise<void>): void {
  _onAccessDenied = cb;
}

let _instanceVersion = 0;
const _instanceListeners = new Set<() => void>();

function _notifyInstanceChange(): void {
  _instanceVersion++;
  _instanceListeners.forEach((l) => l());
}

function _subscribeToInstance(cb: () => void): () => void {
  _instanceListeners.add(cb);
  return () => {
    _instanceListeners.delete(cb);
  };
}

function _getInstanceVersion(): number {
  return _instanceVersion;
}

function getPubNubInstance(): PubNub | null {
  return pubnubInstance;
}

export function initPubNub(pubKey: string, subKey: string, userId: string): void {
  if (pubnubInstance) {
    pubnubInstance.stop();
    pubnubInstance = null;
  }
  pubnubInstance = new PubNub({
    publishKey: pubKey,
    subscribeKey: subKey,
    userId,
  });
  _notifyInstanceChange();
}

export function setPubNubToken(token: string): void {
  if (!pubnubInstance) {
    logger.warn(
      "setPubNubToken called before PubNub was initialized — token discarded",
    );
    return;
  }
  pubnubInstance.setToken(token);
}

export function destroyPubNub(): void {
  if (!pubnubInstance) return;
  pubnubInstance.stop();
  pubnubInstance = null;
}

export function getPubNub(): PubNub | null {
  return pubnubInstance;
}

export interface AppMessage {
  id?: string | null;
  text?: string | null;
  /** file-stc ids + minimal info (no URLs); legacy items may still hold URLs. `link`: the sender's resolved link. */
  attachment?: { media?: ChatMedia[] | null; link?: ChatLink | null; preview?: ChatPreview | null } | null;
  sender_uuid: string;
  sender_name: string;
  avatar?: string | null;
  time_stamp: number;
  channel?: string | null;
  conv_id?: number | null;
  conv_type?: string | null;
  local_id?: string | null;
  reply_to?: {
    message_id?: string;
    local_id?: string | null;
    sender_name?: string | null;
    sender_uuid?: string | null;
    text_preview?: string | null;
    has_attachment?: boolean;
    attachment_type?: string | null;
    time_stamp?: number | null;
    /** The quoted message was deleted. */
    deleted?: boolean;
  } | null;
  reaction?: {
    message_id: string | null;
    local_id: string | null;
    emoji: string;
    action: "add" | "remove";
  } | null;
  /** Delete a sent message: the sender's live broadcast after the REST delete succeeded (no text / attachment). */
  deletion?: {
    message_id?: string | null;
    local_id?: string | null;
  } | null;
  noti_text?: string | null;
}

export function usePubNubVersion(): number {
  return useSyncExternalStore(_subscribeToInstance, _getInstanceVersion);
}

export function usePubNub(
  channel: string | null,
  onMessage: (msg: Message) => void,
  onReaction?: (ev: ReactionEventPayload) => void,
  onDeletion?: (ev: DeletionEventPayload) => void,
) {
  const _version = usePubNubVersion(); // re-run effect when instance changes
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onReactionRef = useRef(onReaction);
  onReactionRef.current = onReaction;
  const onDeletionRef = useRef(onDeletion);
  onDeletionRef.current = onDeletion;
  const idCounterRef = useRef(0);
  const myUuidRef = useRef<string>("");

  useEffect(() => {
    if (!channel) return;
    const pn = getPubNubInstance();
    if (!pn) return; // auth not initialized yet
    myUuidRef.current = pn.getUUID();

    const listener: Listener = {
      message: (event) => {
        const { timetoken } = event;
        const payload = event.message as unknown as AppMessage;
        if (payload.deletion) {
          // Never a row: applied (sender-checked) to the loaded rows, or dropped.
          onDeletionRef.current?.({
            message_id: payload.deletion.message_id ?? null,
            local_id: payload.deletion.local_id ?? null,
            sender_uuid: payload.sender_uuid ?? "",
          });
          return;
        }
        if (payload.reaction) {
          onReactionRef.current?.({
            message_id: payload.reaction.message_id ?? null,
            local_id: payload.reaction.local_id ?? null,
            emoji: payload.reaction.emoji,
            action: payload.reaction.action,
            user_uuid: payload.sender_uuid ?? "",
            user_name: payload.sender_name ?? "",
          });
          return;
        }
        // No content (an event kind this bundle does not know): never a row.
        if (!isRenderableMessage(payload)) return;
        const msg: Message = {
          id: payload.id ?? `${timetoken}-${idCounterRef.current++}`,
          ...mediaFieldsOf(payload.attachment?.media),
          link: readChatLink(payload.attachment?.link),
          preview: readChatPreview(payload.attachment),
          text: payload.text ?? "",
          isSent: payload.sender_uuid === myUuidRef.current,
          senderName: payload.sender_name ?? "",
          senderUuid: payload.sender_uuid ?? "",
          avatar: payload.avatar ?? null,
          timestamp: payload.time_stamp ?? Number(timetoken) / 10000,
          local_id: payload.local_id ?? null,
          replyTo: payload.reply_to
            ? {
                text: payload.reply_to.text_preview ?? "",
                senderName: payload.reply_to.sender_name ?? "",
                senderUuid: payload.reply_to.sender_uuid ?? null,
                referencedMessageId: payload.reply_to.message_id ?? null,
                referencedLocalId: payload.reply_to.local_id ?? null,
                referencedTimestamp: payload.reply_to.time_stamp ?? null,
                attachmentType: payload.reply_to.has_attachment ? (payload.reply_to.attachment_type ?? null) : null,
                ...(payload.reply_to.deleted ? { deleted: true } : {}),
              }
            : null,
        };
        logger.debug(
          "[usePubNub] message on channel:",
          channel,
          "→ onMessageRef.current:",
          onMessageRef.current.name || "(anonymous)",
          "— from:",
          payload.sender_name,
          "id:",
          msg.id,
        );
        onMessageRef.current(msg);
      },
      status: (statusEvent) => {
        if (
          statusEvent.category === "PNAccessDeniedCategory" &&
          _onAccessDenied &&
          !_accessDeniedInProgress
        ) {
          _accessDeniedInProgress = true;
          _onAccessDenied()
            .catch((e) => logger.error(e))
            .finally(() => {
              _accessDeniedInProgress = false;
            });
        }
      },
    };

    pn.addListener(listener);
    pn.subscribe({ channels: [channel] });

    return () => {
      pn.removeListener(listener);
      pn.unsubscribe({ channels: [channel] });
    };
  }, [channel, _version]);

  const publish = useCallback(
    async (ch: string, message: AppMessage): Promise<void> => {
      const pn = getPubNubInstance();
      if (!pn) throw new Error("PubNub not initialized");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await pn.publish({ channel: ch, message: message as any });
    },
    [],
  );

  return { publish };
}
